import {
  SortDirection,
  SortState,
  TableData,
} from "../components/table/Table.Parts";

type StringKey<T> = Extract<keyof T, string>;

type Sort<SortKeyType = string, SortDirectionType = string> = {
  key: SortKeyType | null;
  direction?: SortDirectionType | null;
};

/**
 * A helper component used to manage and manipulate URL search parameters.
 *
 * This helper provides functions to get and set query parameters in the URL.
 * It helps ensure consistent behavior across components that rely on URL-based state management.
 */
export const SearchParamsHelper = {
  /**
   * Retrieves the current page index from the URL search parameters.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   *
   * @returns The current page index (0-based). Defaults to `0` if the `page` parameter is not present.
   */
  getPage: (params: URLSearchParams): number => {
    const pageParam = params.get("page");

    return pageParam ? Number(pageParam) - 1 : 0;
  },

  /**
   * Updates the page index in the URL search parameters.
   *
   * @param params - The current `URLSearchParams` object.
   * @param pageIndex - The new page index (0-based).
   * @param setSearchParams - A function to update the URL search parameters.
   *
   * @returns A void function that updates the page index in the URL.
   *          The page index is set to `pageIndex + 1` to match the 1-based index used in URLs.
   */
  setPage: (
    pageIndex: number,
    params: URLSearchParams,
    setSearchParams: (
      params: URLSearchParams,
      options?: { replace?: boolean },
    ) => void,
  ): void => {
    const searchParams = new URLSearchParams(params);
    searchParams.set("page", String(pageIndex + 1));

    setSearchParams(searchParams, { replace: true });
  },

  /** @todo Decouple from table types */
  /**
   * Retrieves the current sorting configuration from the URL search parameters.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   * @param validateSortKey - A function to validate the sort key.
   *
   * @returns An object representing the current sort configuration:
   *          - `key`: The field being sorted (if valid).
   *          - `direction`: The sort direction (`ASCENDING` or `DESCENDING`).
   *          Defaults to `{ key: undefined, direction: SortDirection.NONE }` if no valid sort is found.
   */
  getSort: <TItem extends TableData>(
    params: URLSearchParams,
    validateSortKey: (value: string) => value is StringKey<TItem>,
  ): SortState<TItem> => {
    const sortParams = params.get("sort")?.split(":");
    const key = sortParams?.[0];
    const direction = sortParams?.[1];

    const isValidDirection =
      direction === SortDirection.ASCENDING ||
      direction === SortDirection.DESCENDING;

    return key && isValidDirection && validateSortKey(key)
      ? { key, direction }
      : { key: undefined, direction: SortDirection.NONE };
  },

  /**
   * Retrieves the current sorting configuration from the URL search parameters using provided key and direction maps.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   * @param sortKeys - An object or enum containing allowed sort keys.
   * @param sortDirections - An object or enum containing allowed sort directions.
   *
   * @returns An object representing the current sort configuration:
   *          - `key`: The key used for the sorting process.
   *          - `direction`: The sort direction.
   *          Defaults to `{ key: undefined, direction: undefined }` if no valid sort is found.
   */
  getSimpleSort: <SortKeyType extends string, SortDirectionType extends string>(
    params: URLSearchParams,
    sortKeys: { [key: string]: SortKeyType },
    sortDirections: { [key: string]: SortDirectionType },
  ): Partial<Sort<SortKeyType, SortDirectionType>> => {
    const sortParams = params.get("sort")?.split(":");
    const key = sortParams?.[0] as SortKeyType;
    const direction = sortParams?.[1] as SortDirectionType;

    if (
      key &&
      Object.values(sortKeys).includes(key) &&
      Object.values(sortDirections).includes(direction)
    )
      return { key, direction };

    return { key: undefined, direction: undefined };
  },

  /** @todo Decouple from table types */
  /**
   * Updates the sorting configuration in the URL search parameters.
   *
   * @param params - The current `URLSearchParams` object.
   * @param sort - The new sort configuration:
   *               - `key`: The field to sort by.
   *               - `direction`: The sort direction (`ASCENDING`, `DESCENDING`, or `NONE`).
   * @param setSearchParams - A function to update the URL search parameters.
   * @param resetPage - Whether to reset the page to `1` when the sort changes. Defaults to `true`.
   *
   * @returns A void function that updates the sort configuration in the URL.
   *          If `resetPage` is `true`, the page index is reset to `1`.
   *          If the sort direction is `NONE`, the `sort` parameter is removed from the URL.
   */
  setSort: <TItem extends TableData>(
    sort: SortState<TItem>,
    params: URLSearchParams,
    setSearchParams: (params: URLSearchParams) => void,
    resetPage = true,
  ): void => {
    const searchParams = new URLSearchParams(params);

    if (sort.direction === SortDirection.NONE) {
      searchParams.delete("sort");
    } else {
      searchParams.set("sort", `${String(sort.key)}:${sort.direction}`);
    }

    if (resetPage) {
      searchParams.set("page", "1");
    }

    setSearchParams(searchParams);
  },

  /**
   * Updates the sorting configuration in the URL search parameters using provided key and direction values.
   *
   * @param sort - The new sort configuration:
   *               - `key`: The field to sort by.
   *               - `direction`: The sort direction (string value or enum).
   * @param params - The current `URLSearchParams` object.
   * @param setSearchParams - A function to update the URL search parameters.
   * @param resetPage - Whether to reset the page to `1` when the sort changes. Defaults to `true`.
   *
   * @returns A void function that updates the sort configuration in the URL.
   *          If `resetPage` is `true`, the page index is reset to `1`.
   *          If the sort direction is not provided, the `sort` parameter is removed from the URL.
   */
  setSimpleSort: (
    sort: Sort,
    params: URLSearchParams,
    setSearchParams: (
      params: URLSearchParams,
      options?: { replace?: boolean },
    ) => void,
    resetPage = true,
  ): void => {
    const searchParams = new URLSearchParams(params);

    if (resetPage) searchParams.set("page", "1");

    if (!sort.direction) searchParams.delete("sort");
    else searchParams.set("sort", `${sort.key}:${sort.direction}`);

    setSearchParams(searchParams, { replace: true });
  },

  /**
   * Retrieves a number array from the URL search parameters.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   * @param key - The key of the parameter to retrieve.
   *
   * @returns An array of numbers parsed from the parameter value. Defaults to an empty array if not found.
   */
  getNumbersFromSearchParams: (
    params: URLSearchParams,
    key: string,
  ): number[] =>
    params
      .getAll(key)
      .map(Number)
      .filter((n) => !isNaN(n)),

  /**
   * Retrieves a string value from the URL search parameters.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   *
   * @returns The string value of the parameter. Defaults to `null` if not found.
   */
  getStringFromSearchParam: (
    params: URLSearchParams,
    key: string,
  ): string | null => params.get(key) || null,

  /**
   * Retrieves the filter configuration from the URL search parameters.
   *
   * @param params - The `URLSearchParams` object containing the query params.
   *
   * @returns An array of filter objects with name and values.
   */
  getFiltersFromSearchParam: <T extends string>(
    params: URLSearchParams,
  ): Array<{ name: T; values: string[] }> => {
    // 1. Get the single 'filter' string instead of getAll()
    const filterParam = params.get("filter");

    if (!filterParam) return [];

    // 2. Split the single string by our new category delimiter "|"
    const filterChunks = filterParam.split("|");

    // 3. Parse each chunk exactly like you did before
    // Values are percent-encoded (see setFiltersToSearchParam) so a value that
    // legitimately contains a "," isn't mistaken for two separate values.
    return filterChunks.map((chunk) => {
      const [name, values = ""] = chunk.split(":");
      return {
        name: name as T,
        // decodeURIComponent throws on malformed percent-encoding (e.g. a
        // stray "%" from a hand-edited or stale URL) - fall back to the raw
        // value instead of crashing the page on this untrusted URL state.
        values: values.split(",").map((value) => {
          try {
            return decodeURIComponent(value);
          } catch {
            return value;
          }
        }),
      };
    });
  },

  /**
   * Updates the filter configuration in the URL search parameters.
   *
   * @param filters - Array of filter objects with name and values.
   * @param params - The current `URLSearchParams` object.
   * @param setSearchParams - A function to update the URL search parameters.
   * @param resetPage - Whether to reset the page to `1` when filters change. Defaults to `true`.
   */
  setFiltersToSearchParam: (
    filters: Array<{ name: string; values: string[] }>,
    params: URLSearchParams,
    setSearchParams: (
      params: URLSearchParams,
      options?: { replace?: boolean },
    ) => void,
    resetPage = true,
  ): void => {
    const searchParams = new URLSearchParams(params);
    searchParams.delete("filter");

    const filterStrings = filters
      .filter(({ values }) => values.length > 0)
      .map(
        ({ name, values }) =>
          `${name}:${values.map(encodeURIComponent).join(",")}`,
      );

    if (filterStrings.length > 0) {
      searchParams.set("filter", filterStrings.join("|"));
    }

    if (resetPage) searchParams.set("page", "1");
    setSearchParams(searchParams, { replace: true });
  },
};
